import React, { useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import './TableViewSelector.scss';
import { useDispatch, useSelector } from 'react-redux';
import type { DataGridType } from '../DataGridTypes.ts';

export type SECTION_CHANGE = 'PREVIOUS' | 'NEXT'

interface TableViewSelectorProps {
  type: DataGridType; // type of table, e.g., 'assets' or 'invoices'
  onViewChange?: (viewType: string) => void;
	onSectionChange?: (action: SECTION_CHANGE) => void;
	sectionPosition?: number;
	isLoading?: boolean;
}

const TableViewSelector: React.FC<TableViewSelectorProps> = ({
  type,
  onViewChange,
	onSectionChange,
 	sectionPosition,
	isLoading = false
}) => {
  const dispatch = useDispatch();
  const selectedView = useSelector(type.selectedView || (() => ''));
  const [internalLoading, setInternalLoading] = useState(false);

  const options = type.tableViewOptions || [];

  const handleViewChange = (event: any) => {
    const newView = event.target.value as string;
    dispatch(type.setViewAction ? type.setViewAction(newView) : { type: '', payload: newView });

    if (onViewChange) {
      onViewChange(newView);
    }
  };

  const handleSectionChange = async (action: SECTION_CHANGE) => {
    if (!onSectionChange) return;

    setInternalLoading(true);
    try {
      await onSectionChange(action);
    } finally {
      // Small delay to prevent flash if data loads too quickly
      setTimeout(() => setInternalLoading(false), 300);
    }
  };

  const loading = isLoading || internalLoading;

  return (
    <Box
      data-testid="table-view-selector"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: (theme) => theme.palette.surface.secondary,
        px: 2,
        py: 1,
        borderRadius: 1,
        mb: 2,
        borderBottom: '1px solid',
        borderColor: (theme) => theme.palette.divider,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150, mr: 2 }} id={`${type}-view-select`}>
          <Select
            variant='standard'
            labelId={`${type}-view-select-label`}
            data-testid="table-view-select"
            value={selectedView}
            onChange={handleViewChange}
          >
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {onSectionChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleSectionChange('PREVIOUS')}
            data-testid="previous-button"
						disabled={sectionPosition === -1 || loading}
          >
						{ loading ? <CircularProgress size={16} color="inherit" /> : 'Previous' }
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleSectionChange('NEXT')}
            data-testid="next-button"
						disabled={sectionPosition === 1 || loading}
          >
						{ loading ? <CircularProgress size={16} color="inherit" /> : 'Next' }
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TableViewSelector;